import { useParams, Link } from "react-router-dom";
import { usePatientDetail, PatientDetail, TriageSubmission } from "@/hooks/usePatientDetail";
import { useAuth } from "@/hooks/useAuth";
import { MedicalNotesCard } from "./MedicalNotesCard";
import { DocumentsCard } from "./DocumentsCard";
import { AppointmentsCard } from "./AppointmentsCard";
import { PatientSummaryCard } from "./PatientSummaryCard";
import { ClinicalIndicatorsCard } from "./ClinicalIndicatorsCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, FileText, Activity, Clock, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

// Using PatientDetail interface from our hook

export function DoctorPatientDashboard() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  
  // Use our comprehensive data fetching hook
  const { data: patient, isLoading, error } = usePatientDetail(id);

  if (isLoading) {
    return <DoctorPatientDashboardLoading />;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Patient Data</h2>
        <p className="text-gray-600 mb-4">
          There was a problem loading the patient information. Please try again later.
        </p>
        <div className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
        <Link to="/patients">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Patients
          </Button>
        </Link>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Patient Not Found</h2>
        <p className="text-gray-600 mb-4">
          The patient you're looking for doesn't exist or you don't have permission to view them.
        </p>
        <Link to="/patients">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Patients
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/patients">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {patient.first_name} {patient.last_name}
            </h1>
            <p className="text-gray-600">Patient Dashboard</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Appointment
          </Button>
          <Button size="sm">
            <FileText className="w-4 h-4 mr-2" />
            Add Medical Note
          </Button>
        </div>
      </div>

      {/* Patient Overview Section */}
      <div className="grid gap-6 md:grid-cols-3">
        <PatientSummaryCard patient={patient} />
        <LatestTriageCard triageHistory={patient.triageHistory} />
        <ClinicalIndicatorsCard patient={patient} />
      </div>

      {/* Detailed Information Tabs */}
      <Tabs defaultValue="medical-history" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="medical-history">Medical History</TabsTrigger>
          <TabsTrigger value="triage-history">Triage History</TabsTrigger>
          <TabsTrigger value="appointments">Appointments</TabsTrigger>
          <TabsTrigger value="clinical-notes">Clinical Notes</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="medical-history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Medical History</CardTitle>
              <CardDescription>Patient's medical background and history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">Medical Conditions</h3>
                  <p className="text-gray-600">{patient.medical_history || "No medical history recorded"}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Allergies</h3>
                  <p className="text-gray-600">{patient.allergies || "No allergies recorded"}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Medications</h3>
                  <p className="text-gray-600">{patient.medications || "No medications recorded"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="triage-history">
          <Card>
            <CardHeader>
              <CardTitle>Triage History</CardTitle>
              <CardDescription>Patient's previous triage assessments</CardDescription>
            </CardHeader>
            <CardContent>
              {patient.triageHistory.length > 0 ? (
                <div className="space-y-4">
                  {patient.triageHistory.map((triage: any) => (
                    <div key={triage.id} className="border rounded-md p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Triage Assessment
                          </h3>
                          <p className="text-sm text-gray-500">
                            {new Date(triage.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge className={
                          triage.urgency === 'immediate' ? 'bg-red-500' :
                          triage.urgency === 'urgent' ? 'bg-orange-500' :
                          triage.urgency === 'standard' ? 'bg-blue-500' :
                          'bg-green-500'
                        }>
                          {triage.urgency}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mb-2">
                        <span className="font-medium">Chief Complaint:</span> {triage.chief_complaint || "Not specified"}
                      </p>
                      <Button variant="outline" size="sm">View Details</Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No triage history available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appointments">
          <AppointmentsCard appointments={patient.appointments} patientId={patient.id} />
        </TabsContent>

        <TabsContent value="clinical-notes">
          <MedicalNotesCard medicalNotes={patient.medicalNotes} patientId={patient.id} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsCard documents={patient.documents} patientId={patient.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper Components

// Local PatientSummaryCard component (will be replaced by imported one)
function LocalPatientSummaryCard({ patient }: { patient: PatientDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Patient Information</CardTitle>
        <CardDescription>Basic patient details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-sm text-gray-500">Full Name</p>
            <p className="font-medium">{patient.first_name} {patient.last_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Date of Birth</p>
            <p className="font-medium">{patient.date_of_birth || "Not specified"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Gender</p>
            <p className="font-medium">{patient.gender || "Not specified"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="font-medium">{patient.email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Phone</p>
            <p className="font-medium">{patient.phone || "Not specified"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Address</p>
            <p className="font-medium">{patient.address || "Not specified"}</p>
          </div>
        </div>
        
        {/* Emergency Contact Information */}
        {(patient.emergency_contact_name || patient.emergency_contact_phone) && (
          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">Emergency Contact</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{patient.emergency_contact_name || "Not specified"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{patient.emergency_contact_phone || "Not specified"}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Medical Information */}
        <div className="mt-4">
          <h3 className="text-sm font-medium mb-2">Medical Information</h3>
          <div className="space-y-2">
            <div>
              <p className="text-sm text-gray-500">Allergies</p>
              <p className="font-medium">{patient.allergies || "None reported"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Medications</p>
              <p className="font-medium">{patient.medications || "None reported"}</p>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" className="w-full">
          Edit Patient Information
        </Button>
      </CardFooter>
    </Card>
  );
}

function LatestTriageCard({ triageHistory }: { triageHistory: TriageSubmission[] }) {
  const latestTriage = triageHistory.length > 0 ? triageHistory[0] : null;

  // Format symptoms for display
  const formatSymptoms = (symptoms: string[] | string | undefined) => {
    if (!symptoms) return "None reported";
    
    if (typeof symptoms === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(symptoms);
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
        return symptoms;
      } catch {
        // If not valid JSON, return as is
        return symptoms;
      }
    }
    
    if (Array.isArray(symptoms)) {
      return symptoms.join(', ');
    }
    
    return String(symptoms);
  };

  // Get urgency display info
  const getUrgencyInfo = (urgency: string) => {
    switch (urgency) {
      case 'immediate':
        return { color: 'bg-red-500', label: 'Immediate' };
      case 'urgent':
        return { color: 'bg-orange-500', label: 'Urgent' };
      case 'standard':
        return { color: 'bg-blue-500', label: 'Standard' };
      case 'non-urgent':
        return { color: 'bg-green-500', label: 'Non-Urgent' };
      default:
        return { color: 'bg-gray-500', label: urgency || 'Unknown' };
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Latest Triage</CardTitle>
          <CardDescription>Most recent assessment</CardDescription>
        </div>
        {latestTriage && (
          <Badge className={getUrgencyInfo(latestTriage.urgency).color}>
            {getUrgencyInfo(latestTriage.urgency).label}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {latestTriage ? (
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-medium">{new Date(latestTriage.created_at).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Chief Complaint</p>
              <p className="font-medium">{latestTriage.chief_complaint || "Not specified"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Symptoms</p>
              <p className="font-medium">{formatSymptoms(latestTriage.symptoms)}</p>
            </div>
            
            {/* Manchester Score */}
            {latestTriage.manchester_score !== undefined && (
              <div>
                <p className="text-sm text-gray-500">Manchester Score</p>
                <p className="font-medium">{latestTriage.manchester_score}</p>
              </div>
            )}
            
            {/* Vital Signs */}
            {latestTriage.vital_signs && (
              <div>
                <p className="text-sm text-gray-500 font-medium">Vital Signs</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
                  {latestTriage.vital_signs.temperature !== undefined && (
                    <div>
                      <p className="text-xs text-gray-500">Temperature</p>
                      <p className="text-sm">{latestTriage.vital_signs.temperature}°C</p>
                    </div>
                  )}
                  {latestTriage.vital_signs.heart_rate !== undefined && (
                    <div>
                      <p className="text-xs text-gray-500">Heart Rate</p>
                      <p className="text-sm">{latestTriage.vital_signs.heart_rate} bpm</p>
                    </div>
                  )}
                  {latestTriage.vital_signs.blood_pressure_systolic !== undefined && 
                   latestTriage.vital_signs.blood_pressure_diastolic !== undefined && (
                    <div>
                      <p className="text-xs text-gray-500">Blood Pressure</p>
                      <p className="text-sm">{latestTriage.vital_signs.blood_pressure_systolic}/{latestTriage.vital_signs.blood_pressure_diastolic} mmHg</p>
                    </div>
                  )}
                  {latestTriage.vital_signs.oxygen_saturation !== undefined && (
                    <div>
                      <p className="text-xs text-gray-500">O₂ Saturation</p>
                      <p className="text-sm">{latestTriage.vital_signs.oxygen_saturation}%</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600">No triage history available</p>
        )}
      </CardContent>
      {latestTriage && (
        <CardFooter>
          <Button variant="outline" size="sm" className="w-full">
            View Full Triage Details
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

// ClinicalIndicatorsCard has been moved to its own file

function DoctorPatientDashboardLoading() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32 mt-2" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-40" />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-2">
              {[...Array(4)].map((_, j) => (
                <div key={j}>
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-40 mt-1" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <Skeleton className="h-10 w-full mb-4" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="border rounded-md p-4">
                  <div className="flex justify-between">
                    <div>
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-24 mt-1" />
                    </div>
                    <Skeleton className="h-6 w-16" />
                  </div>
                  <Skeleton className="h-4 w-full mt-2" />
                  <Skeleton className="h-9 w-28 mt-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
